// hooks/useGlobalBarcodeScanner.ts
import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';

// Fixed window name for the check-in tab. Reusing one name (instead of '_blank')
// makes every scan navigate the same tab rather than opening a new one.
const CHECKIN_TAB_NAME = 'fortius-checkin';

export const useGlobalBarcodeScanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bufferRef = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastScanRef = useRef<number>(0);

  const processMembershipId = useCallback((membershipId: string) => {
    // Prevent duplicate scans within 2 seconds
    const now = Date.now();
    if (now - lastScanRef.current < 2000) return;
    lastScanRef.current = now;

    console.log('Processing membership ID:', membershipId);
    console.log('Current location:', location.pathname);

    // Barcode scanner always triggers check-in mode
    const targetUrl = `/profiles/${membershipId}?checkin=true`;
    console.log('Barcode scanner detected, navigating to profile with check-in enabled:', targetUrl);
    
    // Check if we're already on a profiles page
    if (location.pathname.startsWith('/profiles')) {
      console.log('Already on profiles page, navigating within same tab');
      // Navigate within the same tab
      message.loading({ 
        content: 'Loading member for check-in...', 
        key: 'member-load' 
      });
      navigate(targetUrl);
      
      setTimeout(() => {
        message.destroy('member-load');
      }, 500);
    } else {
      console.log('Not on profiles page, surfacing the check-in tab');
      // Open the member in a dedicated, reused check-in tab. Using a fixed
      // window name (rather than '_blank') means every scan navigates the SAME
      // tab instead of spawning a new one — otherwise a day of scans piles up
      // dozens of tabs. We surface that tab via checkinTab.focus() below and
      // deliberately dropped the trailing window.focus() that used to re-focus
      // the *origin* tab, so focus now ends on the check-in tab.
      message.info({
        content: 'Opening member check-in...',
        key: 'member-load'
      });

      const checkinTab = window.open(targetUrl, CHECKIN_TAB_NAME);
      if (checkinTab) {
        checkinTab.focus();
        console.log('Check-in tab surfaced successfully');
      } else {
        // Fallback if popups are blocked: navigate in the current tab.
        console.log('Popup blocked, navigating in current tab');
        message.warning({
          content: 'Popup blocked. Navigating in current tab...',
          key: 'member-load'
        });
        navigate(targetUrl);
      }

      setTimeout(() => {
        message.destroy('member-load');
      }, 1000);
    }
  }, [navigate, location.pathname]);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    // Skip if user is typing in an input field
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable ||
        target.closest('[contenteditable="true"]')) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    bufferRef.current += event.key;

    // Check for complete barcode (ends with Enter or carriage return)
    if (event.key === 'Enter' || event.key === '\r' || event.key === '\n') {
      let barcode = bufferRef.current
        .replace(/[\r\n]/g, '')
        .replace(/Enter/g, '')
        .trim();
      
      console.log('Raw barcode buffer:', bufferRef.current);
      console.log('Processed barcode:', barcode);
      
      // Remove scanner prefix if present
      barcode = barcode.replace(/^BC:/, '');
      console.log('Barcode after prefix removal:', barcode);
      
      // Validate membership ID (5+ digits only)
      if (/^\d{5,}$/.test(barcode)) {
        console.log('Valid membership ID detected:', barcode);
        event.preventDefault(); // Prevent any default behavior
        processMembershipId(barcode);
      } else {
        console.log('Invalid barcode format:', barcode);
      }
      
      bufferRef.current = '';
      return;
    }

    // Clear buffer after timeout if no Enter received
    timeoutRef.current = setTimeout(() => {
      bufferRef.current = '';
    }, 150); // Slightly longer timeout for reliability
  }, [processMembershipId]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyPress]);
};